'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import Link from 'next/link';
import { ReactionOutcome, classifyReaction } from '@/lib/clipConfigs';

// ── Types ─────────────────────────────────────────────────────────────────────

interface TriggerEntry {
  id: string;
  timeSeconds: number;
}

interface SavedScenario {
  id: string;
  name: string;
  triggers: { id: string; timeSeconds: number }[];
  reactionWindowMs: number;
  fps: number;
  videoFileName: string;
  savedAt: number;
}

type BuilderStep = 'load' | 'tag' | 'play';

// Shot type detection
type ShotType = 'tap' | 'spray' | 'followup';

interface ShotRecord {
  id: number;
  type: ShotType;
  outcome: ReactionOutcome;
  reactionMs: number | null;
  holdMs?: number;
  followupMs?: number;
  timestamp: number;
}

interface TriggerResult {
  triggerIndex: number;
  outcome: ReactionOutcome;
  reactionMs: number | null;
}

// ── Constants ─────────────────────────────────────────────────────────────────

const SCENARIOS_STORAGE_KEY = 'custom_scenarios_v2';
const ACCENT = '#ff8c42';
const SPRAY_THRESHOLD_MS = 150;
const AUTO_ADVANCE_MS = 3000;

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatTimestamp(seconds: number): string {
  const totalMs = Math.round(seconds * 1000);
  const ms = totalMs % 1000;
  const totalSec = Math.floor(totalMs / 1000);
  const sec = totalSec % 60;
  const min = Math.floor(totalSec / 60);
  return `${String(min).padStart(2, '0')}:${String(sec).padStart(2, '0')}.${String(ms).padStart(3, '0')}`;
}

function getResultContent(outcome: ReactionOutcome, reactionMs: number | null) {
  switch (outcome) {
    case 'perfect':
      return { title: 'Perfect', value: reactionMs !== null ? `${reactionMs} ms` : null, desc: "Perfect — you're in elite reaction time range.", isSuccess: true };
    case 'good':
      return { title: 'Good', value: reactionMs !== null ? `${reactionMs} ms` : null, desc: 'Good — you scored an above-average reaction time.', isSuccess: true };
    case 'okay':
      return { title: 'Okay', value: reactionMs !== null ? `${reactionMs} ms` : null, desc: 'Okay — you scored an average reaction time.', isSuccess: true };
    case 'too-early':
      return { title: 'Too Early', value: null, desc: 'Too early — you shot too soon and exposed your position.', isSuccess: false };
    case 'too-late':
      return { title: 'Too Late', value: null, desc: 'Too late — you died.', isSuccess: false };
  }
}

function hexToRgba(hex: string, alpha: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

function outcomeColorCS(outcome: ReactionOutcome, accent: string): string {
  if (outcome === 'too-early' || outcome === 'too-late') return '#ff4655';
  if (outcome === 'perfect') return accent;
  if (outcome === 'good') return '#00e676';
  return 'rgba(255,255,255,0.6)';
}

function outcomeLabelCS(outcome: ReactionOutcome): string {
  if (outcome === 'too-early') return 'Early';
  if (outcome === 'too-late') return 'Late';
  if (outcome === 'perfect') return 'Perfect';
  if (outcome === 'good') return 'Good';
  return 'Okay';
}

function getOutcomeLabel(outcome: ReactionOutcome, reactionMs: number | null): string {
  const label = outcomeLabelCS(outcome);
  if (reactionMs !== null) return `${label} · ${reactionMs}ms`;
  return label;
}

function loadScenariosFromStorage(): SavedScenario[] {
  try {
    const raw = localStorage.getItem(SCENARIOS_STORAGE_KEY);
    if (!raw) return [];
    return JSON.parse(raw) as SavedScenario[];
  } catch { return []; }
}

function saveScenariosToStorage(scenarios: SavedScenario[]) {
  try {
    localStorage.setItem(SCENARIOS_STORAGE_KEY, JSON.stringify(scenarios));
  } catch { /* ignore */ }
}

// ── Crosshair ─────────────────────────────────────────────────────────────────

function CenterCrosshair() {
  const GAP = 5;
  const LINE = 12;
  const T = 1.5;
  const color = '#00d4ff';
  const glow = `0 0 3px ${color}`;
  const size = (GAP + LINE) * 2;
  return (
    <div className="pointer-events-none absolute z-30" style={{ left: '50%', top: '50%', transform: 'translate(-50%, -50%)', width: size, height: size }}>
      <div style={{ position: 'absolute', left: '50%', top: '50%', width: 3, height: 3, background: color, borderRadius: '50%', transform: 'translate(-50%, -50%)', boxShadow: `0 0 4px ${color}` }} />
      <div style={{ position: 'absolute', left: '50%', bottom: `calc(50% + ${GAP}px)`, width: T, height: LINE, background: color, transform: 'translateX(-50%)', boxShadow: glow }} />
      <div style={{ position: 'absolute', left: '50%', top: `calc(50% + ${GAP}px)`, width: T, height: LINE, background: color, transform: 'translateX(-50%)', boxShadow: glow }} />
      <div style={{ position: 'absolute', top: '50%', right: `calc(50% + ${GAP}px)`, height: T, width: LINE, background: color, transform: 'translateY(-50%)', boxShadow: glow }} />
      <div style={{ position: 'absolute', top: '50%', left: `calc(50% + ${GAP}px)`, height: T, width: LINE, background: color, transform: 'translateY(-50%)', boxShadow: glow }} />
      <div style={{ position: 'absolute', inset: 0, borderRadius: '50%', border: '1px solid rgba(0,212,255,0.22)' }} />
    </div>
  );
}

// ── Floating Inline Feedback ──────────────────────────────────────────────────

interface FloatingFeedbackItemProps {
  feedback: FloatingFeedback;
  accentColor: string;
  onDone: (id: number) => void;
}

function FloatingFeedbackItem({ feedback, accentColor, onDone }: FloatingFeedbackItemProps) {
  const [opacity, setOpacity] = useState(1);
  const isSuccess = feedback.outcome !== 'too-early' && feedback.outcome !== 'too-late';
  const color = isSuccess ? accentColor : '#ff4655';

  useEffect(() => {
    const fadeTimer = setTimeout(() => setOpacity(0), 700);
    const removeTimer = setTimeout(() => onDone(feedback.id), 1100);
    return () => { clearTimeout(fadeTimer); clearTimeout(removeTimer); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feedback.id]);

  return (
    <div
      className="pointer-events-none"
      style={{
        opacity,
        transition: 'opacity 0.4s ease-out',
        background: 'rgba(8,8,12,0.88)',
        border: `1px solid ${hexToRgba(color, 0.5)}`,
        borderRadius: 10,
        padding: '6px 14px',
        boxShadow: `0 0 16px ${hexToRgba(color, 0.25)}`,
        backdropFilter: 'blur(6px)',
        whiteSpace: 'nowrap',
      }}>
      <span className="text-sm font-bold font-mono" style={{ color, textShadow: `0 0 10px ${hexToRgba(color, 0.6)}` }}>
        {getOutcomeLabel(feedback.outcome, feedback.reactionMs)}
      </span>
    </div>
  );
}

// ── Single-Trigger Result Card ────────────────────────────────────────────────

interface ResultCardProps {
  data: ResultCardData;
  autoplay: boolean;
  accentColor: string;
  onToggleAutoplay: () => void;
  onNext: () => void;
  onRetry: () => void;
  onClose: () => void;
}

function ResultCard({ data, autoplay, accentColor, onToggleAutoplay, onNext, onRetry, onClose }: ResultCardProps) {
  const [countdown, setCountdown] = useState<number | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const autoRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const content = getResultContent(data.outcome, data.reactionMs);
  const borderColor = hexToRgba(accentColor, 0.45);
  const glowColor = content.isSuccess ? hexToRgba(accentColor, 0.12) : hexToRgba(accentColor, 0.06);

  const clearTimers = useCallback(() => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (autoRef.current) { clearTimeout(autoRef.current); autoRef.current = null; }
  }, []);

  useEffect(() => {
    clearTimers();
    if (autoplay) {
      let remaining = Math.ceil(AUTO_ADVANCE_MS / 1000);
      setCountdown(remaining);
      timerRef.current = setInterval(() => {
        remaining -= 1;
        setCountdown(remaining);
        if (remaining <= 0) clearTimers();
      }, 1000);
      autoRef.current = setTimeout(() => {
        clearTimers();
        setCountdown(null);
        onNext();
      }, AUTO_ADVANCE_MS);
    } else {
      setCountdown(null);
    }
    return clearTimers;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoplay, data]);

  const handleNext = () => { clearTimers(); setCountdown(null); onNext(); };
  const handleRetry = () => { clearTimers(); setCountdown(null); onRetry(); };
  const handleClose = () => { clearTimers(); setCountdown(null); onClose(); };

  return (
    <div className="absolute inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.72)', backdropFilter: 'blur(4px)' }}>
      <div className="relative flex flex-col items-center gap-5 rounded-2xl px-8 py-8 text-center"
        style={{
          background: 'rgba(12,12,18,0.97)',
          border: `1.5px solid ${borderColor}`,
          boxShadow: `0 0 40px ${glowColor}, 0 8px 48px rgba(0,0,0,0.7), inset 0 1px 0 rgba(255,255,255,0.06)`,
          width: '100%', maxWidth: 360, minWidth: 280,
        }}>
        <div className="pointer-events-none absolute inset-x-0 top-0 h-px rounded-t-2xl"
          style={{ background: `linear-gradient(90deg, transparent, ${borderColor}, transparent)` }} />
        <button onClick={handleClose}
          className="absolute top-3 right-3 flex items-center justify-center rounded-lg transition-all"
          style={{ width: 28, height: 28, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.45)', cursor: 'pointer' }}
          onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,70,85,0.15)'; (e.currentTarget as HTMLButtonElement).style.color = '#ff4655'; (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(255,70,85,0.35)'; }}
          onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.06)'; (e.currentTarget as HTMLButtonElement).style.color = 'rgba(255,255,255,0.45)'; (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(255,255,255,0.12)'; }}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
        <div className="flex flex-col items-center gap-1">
          <h2 className="text-4xl font-extrabold tracking-tight"
            style={{ color: content.isSuccess ? accentColor : '#ff4655', textShadow: content.isSuccess ? `0 0 24px ${hexToRgba(accentColor, 0.5)}` : '0 0 24px rgba(255,70,85,0.5)', letterSpacing: '-0.02em' }}>
            {content.title}
          </h2>
          {content.value && (
            <span className="text-2xl font-bold font-mono" style={{ color: '#00e676', textShadow: '0 0 16px #00e67666' }}>
              {content.value}
            </span>
          )}
        </div>
        <p className="text-sm leading-relaxed" style={{ color: 'rgba(255,255,255,0.5)', maxWidth: 280 }}>{content.desc}</p>
        <div className="flex w-full flex-col gap-2.5 mt-1">
          <button onClick={handleNext}
            className="w-full rounded-xl py-3.5 text-sm font-bold uppercase tracking-widest transition-all duration-150"
            style={{ background: hexToRgba(accentColor, 0.15), border: `1.5px solid ${hexToRgba(accentColor, 0.45)}`, color: accentColor, letterSpacing: '0.1em', boxShadow: `0 0 16px ${hexToRgba(accentColor, 0.15)}`, cursor: 'pointer' }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = hexToRgba(accentColor, 0.25); }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = hexToRgba(accentColor, 0.15); }}>
            {autoplay && countdown !== null && countdown > 0 ? `Next… (${countdown})` : 'Next'}
          </button>
          <button onClick={handleRetry}
            className="w-full rounded-xl py-3 text-sm font-bold uppercase tracking-widest transition-all duration-150"
            style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.55)', letterSpacing: '0.1em', cursor: 'pointer' }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.1)'; (e.currentTarget as HTMLButtonElement).style.color = 'rgba(255,255,255,0.85)'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.05)'; (e.currentTarget as HTMLButtonElement).style.color = 'rgba(255,255,255,0.55)'; }}>
            Retry
          </button>
        </div>
        <div className="flex items-center gap-3 mt-1">
          <span className="text-xs font-mono uppercase tracking-widest" style={{ color: 'rgba(255,255,255,0.35)', fontSize: '10px' }}>Autoplay</span>
          <button onClick={onToggleAutoplay}
            className="relative flex items-center rounded-full transition-all duration-200"
            style={{ width: 36, height: 20, background: autoplay ? hexToRgba(accentColor, 0.3) : 'rgba(255,255,255,0.1)', border: `1px solid ${autoplay ? hexToRgba(accentColor, 0.5) : 'rgba(255,255,255,0.15)'}`, cursor: 'pointer', padding: 0 }}>
            <span className="absolute rounded-full transition-all duration-200"
              style={{ width: 14, height: 14, background: autoplay ? accentColor : 'rgba(255,255,255,0.4)', left: autoplay ? 19 : 2, boxShadow: autoplay ? `0 0 6px ${hexToRgba(accentColor, 0.6)}` : 'none' }} />
          </button>
          <span className="text-xs font-mono" style={{ color: autoplay ? accentColor : 'rgba(255,255,255,0.25)', fontSize: '10px' }}>{autoplay ? 'ON' : 'OFF'}</span>
        </div>
      </div>
    </div>
  );
}

// ── Multi-Trigger Summary Card ────────────────────────────────────────────────

interface SummaryCardProps {
  data: SummaryCardData;
  autoplay: boolean;
  accentColor: string;
  onToggleAutoplay: () => void;
  onNext: () => void;
  onRetry: () => void;
  onClose: () => void;
}

function SummaryCard({ data, autoplay, accentColor, onToggleAutoplay, onNext, onRetry, onClose }: SummaryCardProps) {
  const [countdown, setCountdown] = useState<number | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const autoRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const borderColor = hexToRgba(accentColor, 0.45);
  const glowColor = hexToRgba(accentColor, 0.12);

  const kills = data.results.filter(r => r.outcome !== 'too-early' && r.outcome !== 'too-late').length;
  const deaths = data.results.filter(r => r.outcome === 'too-early' || r.outcome === 'too-late').length;
  const successMs = data.results.filter(r => r.reactionMs !== null).map(r => r.reactionMs as number);
  const avgMs = successMs.length > 0 ? Math.round(successMs.reduce((a, b) => a + b, 0) / successMs.length) : null;
  const bestMs = successMs.length > 0 ? Math.min(...successMs) : null;

  const clearTimers = useCallback(() => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    if (autoRef.current) { clearTimeout(autoRef.current); autoRef.current = null; }
  }, []);

  useEffect(() => {
    clearTimers();
    if (autoplay) {
      let remaining = Math.ceil(AUTO_ADVANCE_MS / 1000);
      setCountdown(remaining);
      timerRef.current = setInterval(() => {
        remaining -= 1;
        setCountdown(remaining);
        if (remaining <= 0) clearTimers();
      }, 1000);
      autoRef.current = setTimeout(() => {
        clearTimers();
        setCountdown(null);
        onNext();
      }, AUTO_ADVANCE_MS);
    } else {
      setCountdown(null);
    }
    return clearTimers;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoplay, data]);

  const handleNext = () => { clearTimers(); setCountdown(null); onNext(); };
  const handleRetry = () => { clearTimers(); setCountdown(null); onRetry(); };
  const handleClose = () => { clearTimers(); setCountdown(null); onClose(); };

  const outcomeColor = (outcome: ReactionOutcome) => {
    if (outcome === 'too-early' || outcome === 'too-late') return '#ff4655';
    if (outcome === 'perfect') return accentColor;
    if (outcome === 'good') return '#00e676';
    return 'rgba(255,255,255,0.6)';
  };

  return (
    <div className="absolute inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.72)', backdropFilter: 'blur(4px)' }}>
      <div className="relative flex flex-col gap-4 rounded-2xl px-7 py-7"
        style={{
          background: 'rgba(12,12,18,0.97)',
          border: `1.5px solid ${borderColor}`,
          boxShadow: `0 0 40px ${glowColor}, 0 8px 48px rgba(0,0,0,0.7), inset 0 1px 0 rgba(255,255,255,0.06)`,
          width: '100%', maxWidth: 400, minWidth: 300, maxHeight: '85vh', overflowY: 'auto',
        }}>
        <div className="pointer-events-none absolute inset-x-0 top-0 h-px rounded-t-2xl"
          style={{ background: `linear-gradient(90deg, transparent, ${borderColor}, transparent)` }} />
        <button onClick={handleClose}
          className="absolute top-3 right-3 flex items-center justify-center rounded-lg transition-all"
          style={{ width: 28, height: 28, background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.45)', cursor: 'pointer' }}
          onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,70,85,0.15)'; (e.currentTarget as HTMLButtonElement).style.color = '#ff4655'; (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(255,70,85,0.35)'; }}
          onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.06)'; (e.currentTarget as HTMLButtonElement).style.color = 'rgba(255,255,255,0.45)'; (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(255,255,255,0.12)'; }}>
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
        <div className="text-center pr-6">
          <h2 className="text-2xl font-extrabold tracking-tight text-white mb-0.5" style={{ letterSpacing: '-0.02em' }}>Results</h2>
          <p className="text-xs font-mono uppercase tracking-widest" style={{ color: hexToRgba(accentColor, 0.7) }}>
            {data.results.length} trigger{data.results.length !== 1 ? 's' : ''} completed
          </p>
        </div>
        <div className="grid grid-cols-2 gap-2.5">
          <div className="flex flex-col items-center gap-1 rounded-xl py-3 px-2"
            style={{ background: 'rgba(0,230,118,0.07)', border: '1px solid rgba(0,230,118,0.2)' }}>
            <span className="text-2xl font-extrabold" style={{ color: '#00e676' }}>{kills}</span>
            <span className="text-xs font-mono uppercase tracking-widest" style={{ color: 'rgba(255,255,255,0.4)', fontSize: '9px' }}>Kills</span>
          </div>
          <div className="flex flex-col items-center gap-1 rounded-xl py-3 px-2"
            style={{ background: 'rgba(255,70,85,0.07)', border: '1px solid rgba(255,70,85,0.2)' }}>
            <span className="text-2xl font-extrabold" style={{ color: '#ff4655' }}>{deaths}</span>
            <span className="text-xs font-mono uppercase tracking-widest" style={{ color: 'rgba(255,255,255,0.4)', fontSize: '9px' }}>Deaths</span>
          </div>
          {avgMs !== null && (
            <div className="flex flex-col items-center gap-1 rounded-xl py-3 px-2"
              style={{ background: hexToRgba(accentColor, 0.07), border: `1px solid ${hexToRgba(accentColor, 0.2)}` }}>
              <span className="text-2xl font-extrabold font-mono" style={{ color: accentColor }}>{avgMs}ms</span>
              <span className="text-xs font-mono uppercase tracking-widest" style={{ color: 'rgba(255,255,255,0.4)', fontSize: '9px' }}>Avg RT</span>
            </div>
          )}
          {bestMs !== null && (
            <div className="flex flex-col items-center gap-1 rounded-xl py-3 px-2"
              style={{ background: 'rgba(255,214,0,0.07)', border: '1px solid rgba(255,214,0,0.2)' }}>
              <span className="text-2xl font-extrabold font-mono" style={{ color: '#ffd600' }}>{bestMs}ms</span>
              <span className="text-xs font-mono uppercase tracking-widest" style={{ color: 'rgba(255,255,255,0.4)', fontSize: '9px' }}>Best RT</span>
            </div>
          )}
        </div>
        <div className="rounded-xl overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.07)' }}>
          <div className="px-3 py-2" style={{ background: 'rgba(255,255,255,0.03)', borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
            <p className="text-xs font-mono uppercase tracking-widest" style={{ color: 'rgba(255,255,255,0.3)', fontSize: '9px' }}>Per-Trigger Breakdown</p>
          </div>
          <div className="divide-y" style={{ borderColor: 'rgba(255,255,255,0.04)' }}>
            {data.results.map((r) => {
              const content = getResultContent(r.outcome, r.reactionMs);
              return (
                <div key={r.triggerIndex} className="flex items-center justify-between px-3 py-2.5">
                  <div className="flex items-center gap-2.5">
                    <span className="text-xs font-mono w-4 text-center" style={{ color: 'rgba(255,255,255,0.3)' }}>{r.triggerIndex + 1}</span>
                    <span className="text-sm font-bold" style={{ color: outcomeColor(r.outcome) }}>{content.title}</span>
                  </div>
                  {r.reactionMs !== null && (
                    <span className="text-xs font-mono font-bold" style={{ color: '#00e676' }}>{r.reactionMs}ms</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
        <div className="flex flex-col gap-2.5">
          <button onClick={handleNext}
            className="w-full rounded-xl py-3.5 text-sm font-bold uppercase tracking-widest transition-all duration-150"
            style={{ background: hexToRgba(accentColor, 0.15), border: `1.5px solid ${hexToRgba(accentColor, 0.45)}`, color: accentColor, letterSpacing: '0.1em', boxShadow: `0 0 16px ${hexToRgba(accentColor, 0.15)}`, cursor: 'pointer' }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = hexToRgba(accentColor, 0.25); }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = hexToRgba(accentColor, 0.15); }}>
            {autoplay && countdown !== null && countdown > 0 ? `Play Again… (${countdown})` : 'Play Again'}
          </button>
          <button onClick={handleRetry}
            className="w-full rounded-xl py-3 text-sm font-bold uppercase tracking-widest transition-all duration-150"
            style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.55)', letterSpacing: '0.1em', cursor: 'pointer' }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.1)'; (e.currentTarget as HTMLButtonElement).style.color = 'rgba(255,255,255,0.85)'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.05)'; (e.currentTarget as HTMLButtonElement).style.color = 'rgba(255,255,255,0.55)'; }}>
            Retry
          </button>
        </div>
        <div className="flex items-center justify-center gap-3">
          <span className="text-xs font-mono uppercase tracking-widest" style={{ color: 'rgba(255,255,255,0.35)', fontSize: '10px' }}>Autoplay</span>
          <button onClick={onToggleAutoplay}
            className="relative flex items-center rounded-full transition-all duration-200"
            style={{ width: 36, height: 20, background: autoplay ? hexToRgba(accentColor, 0.3) : 'rgba(255,255,255,0.1)', border: `1px solid ${autoplay ? hexToRgba(accentColor, 0.5) : 'rgba(255,255,255,0.15)'}`, cursor: 'pointer', padding: 0 }}>
            <span className="absolute rounded-full transition-all duration-200"
              style={{ width: 14, height: 14, background: autoplay ? accentColor : 'rgba(255,255,255,0.4)', left: autoplay ? 19 : 2, boxShadow: autoplay ? `0 0 6px ${hexToRgba(accentColor, 0.6)}` : 'none' }} />
          </button>
          <span className="text-xs font-mono" style={{ color: autoplay ? accentColor : 'rgba(255,255,255,0.25)', fontSize: '10px' }}>{autoplay ? 'ON' : 'OFF'}</span>
        </div>
      </div>
    </div>
  );
}

// ── Top Stats Bar ─────────────────────────────────────────────────────────────

interface StatsBarCSProps {
  shots: ShotRecord[];
  accentColor: string;
  totalTriggers: number;
  currentTrigger: number;
}

function StatsBarCS({ shots, accentColor, totalTriggers, currentTrigger }: StatsBarCSProps) {
  const hits = shots.filter(s => s.outcome !== 'too-early' && s.outcome !== 'too-late');
  const misses = shots.filter(s => s.outcome === 'too-early' || s.outcome === 'too-late');
  const reactionTimes = hits.filter(s => s.reactionMs !== null).map(s => s.reactionMs as number);
  const avgMs = reactionTimes.length > 0 ? Math.round(reactionTimes.reduce((a, b) => a + b, 0) / reactionTimes.length) : null;
  const bestMs = reactionTimes.length > 0 ? Math.min(...reactionTimes) : null;
  let streak = 0;
  for (let i = shots.length - 1; i >= 0; i--) {
    if (shots[i].outcome !== 'too-early' && shots[i].outcome !== 'too-late') streak++;
    else break;
  }
  const lastShot = shots[shots.length - 1] ?? null;

  return (
    <div className="absolute top-0 left-0 right-0 z-40 flex items-center gap-0 pointer-events-none"
      style={{ background: 'linear-gradient(to bottom, rgba(8,8,12,0.92) 0%, rgba(8,8,12,0.7) 80%, transparent 100%)', paddingTop: 8, paddingBottom: 16, paddingLeft: 12, paddingRight: 12 }}>
      <div className="flex items-center gap-2 flex-wrap" style={{ flex: 1 }}>
        <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg"
          style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)' }}>
          <span className="text-xs font-mono font-bold" style={{ color: accentColor }}>{currentTrigger + 1}</span>
          <span className="text-xs font-mono" style={{ color: 'rgba(255,255,255,0.3)' }}>/</span>
          <span className="text-xs font-mono" style={{ color: 'rgba(255,255,255,0.5)' }}>{totalTriggers}</span>
        </div>
        <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg"
          style={{ background: 'rgba(0,230,118,0.08)', border: '1px solid rgba(0,230,118,0.2)' }}>
          <span className="text-xs font-mono" style={{ color: 'rgba(0,230,118,0.7)', fontSize: '9px', letterSpacing: '0.08em' }}>HIT</span>
          <span className="text-xs font-mono font-bold" style={{ color: '#00e676' }}>{hits.length}</span>
        </div>
        <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg"
          style={{ background: 'rgba(255,70,85,0.08)', border: '1px solid rgba(255,70,85,0.2)' }}>
          <span className="text-xs font-mono" style={{ color: 'rgba(255,70,85,0.7)', fontSize: '9px', letterSpacing: '0.08em' }}>MISS</span>
          <span className="text-xs font-mono font-bold" style={{ color: '#ff4655' }}>{misses.length}</span>
        </div>
        {streak > 1 && (
          <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg"
            style={{ background: hexToRgba(accentColor, 0.08), border: `1px solid ${hexToRgba(accentColor, 0.25)}` }}>
            <span className="text-xs font-mono" style={{ color: hexToRgba(accentColor, 0.7), fontSize: '9px', letterSpacing: '0.08em' }}>STREAK</span>
            <span className="text-xs font-mono font-bold" style={{ color: accentColor }}>{streak}×</span>
          </div>
        )}
        {avgMs !== null && (
          <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg"
            style={{ background: hexToRgba(accentColor, 0.06), border: `1px solid ${hexToRgba(accentColor, 0.18)}` }}>
            <span className="text-xs font-mono" style={{ color: hexToRgba(accentColor, 0.6), fontSize: '9px', letterSpacing: '0.08em' }}>AVG</span>
            <span className="text-xs font-mono font-bold" style={{ color: accentColor }}>{avgMs}ms</span>
          </div>
        )}
        {bestMs !== null && (
          <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg"
            style={{ background: 'rgba(255,214,0,0.06)', border: '1px solid rgba(255,214,0,0.18)' }}>
            <span className="text-xs font-mono" style={{ color: 'rgba(255,214,0,0.6)', fontSize: '9px', letterSpacing: '0.08em' }}>BEST</span>
            <span className="text-xs font-mono font-bold" style={{ color: '#ffd600' }}>{bestMs}ms</span>
          </div>
        )}
        {lastShot && (
          <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg"
            style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.1)' }}>
            <span className="text-xs font-mono" style={{ color: 'rgba(255,255,255,0.4)', fontSize: '9px', letterSpacing: '0.08em' }}>
              {lastShot.type === 'tap' ? 'TAP' : lastShot.type === 'spray' ? 'SPRAY' : 'FOLLOW'}
            </span>
            {lastShot.reactionMs !== null && (
              <span className="text-xs font-mono font-bold" style={{ color: outcomeColorCS(lastShot.outcome, accentColor) }}>{lastShot.reactionMs}ms</span>
            )}
            {lastShot.type === 'spray' && lastShot.holdMs !== undefined && (
              <span className="text-xs font-mono" style={{ color: 'rgba(255,255,255,0.35)', fontSize: '9px' }}>hold:{lastShot.holdMs}ms</span>
            )}
            {lastShot.type === 'followup' && lastShot.followupMs !== undefined && (
              <span className="text-xs font-mono" style={{ color: 'rgba(255,255,255,0.35)', fontSize: '9px' }}>gap:{lastShot.followupMs}ms</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ── Recent Shots List ─────────────────────────────────────────────────────────

interface RecentShotsListCSProps {
  shots: ShotRecord[];
  accentColor: string;
}

function RecentShotsListCS({ shots, accentColor }: RecentShotsListCSProps) {
  const recent = shots.slice(-8).reverse();
  if (recent.length === 0) return null;
  return (
    <div className="absolute left-3 z-35 flex flex-col gap-1 pointer-events-none"
      style={{ top: '50%', transform: 'translateY(-50%)', maxWidth: 160 }}>
      <p className="text-xs font-mono uppercase tracking-widest mb-1" style={{ color: 'rgba(255,255,255,0.25)', fontSize: '8px' }}>Recent Shots</p>
      {recent.map((shot, i) => {
        const color = outcomeColorCS(shot.outcome, accentColor);
        const isLatest = i === 0;
        return (
          <div key={shot.id} className="flex items-center gap-1.5 px-2 py-1 rounded-lg"
            style={{ background: isLatest ? hexToRgba(color, 0.12) : 'rgba(8,8,12,0.75)', border: `1px solid ${isLatest ? hexToRgba(color, 0.35) : 'rgba(255,255,255,0.07)'}`, opacity: 1 - i * 0.1, backdropFilter: 'blur(4px)' }}>
            <span className="text-xs font-mono font-bold" style={{ color, fontSize: '8px', letterSpacing: '0.06em', minWidth: 28 }}>
              {shot.type === 'tap' ? 'TAP' : shot.type === 'spray' ? 'SPR' : 'FLW'}
            </span>
            <span className="text-xs font-mono font-bold" style={{ color, fontSize: '9px' }}>{outcomeLabelCS(shot.outcome)}</span>
            {shot.reactionMs !== null && (
              <span className="text-xs font-mono" style={{ color: 'rgba(255,255,255,0.5)', fontSize: '9px' }}>{shot.reactionMs}ms</span>
            )}
            {shot.type === 'spray' && shot.holdMs !== undefined && (
              <span className="text-xs font-mono" style={{ color: 'rgba(255,255,255,0.3)', fontSize: '8px' }}>/{shot.holdMs}ms</span>
            )}
            {shot.type === 'followup' && shot.followupMs !== undefined && (
              <span className="text-xs font-mono" style={{ color: 'rgba(255,255,255,0.3)', fontSize: '8px' }}>+{shot.followupMs}ms</span>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Timestamp Readout (live, uses rAF) ────────────────────────────────────────

function TimestampReadout({ videoRef }: { videoRef: React.RefObject<HTMLVideoElement | null> }) {
  const [display, setDisplay] = useState('00:00.000');
  const [decimalSec, setDecimalSec] = useState('0.000');
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    const update = () => {
      const v = videoRef.current;
      if (v) {
        const t = v.currentTime;
        setDisplay(formatTimestamp(t));
        setDecimalSec(t.toFixed(3));
      }
      rafRef.current = requestAnimationFrame(update);
    };
    rafRef.current = requestAnimationFrame(update);
    return () => { if (rafRef.current !== null) cancelAnimationFrame(rafRef.current); };
  }, [videoRef]);

  return (
    <div className="flex items-center gap-3 px-4 py-3 rounded-xl" style={{ background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.06)' }}>
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" />
      </svg>
      <span className="text-sm font-mono font-bold text-white">{display}</span>
      <span className="text-xs font-mono" style={{ color: 'rgba(255,255,255,0.35)' }}>({decimalSec}s)</span>
    </div>
  );
}

// ── Seek Bar with Trigger Markers ─────────────────────────────────────────────

interface SeekBarProps {
  videoRef: React.RefObject<HTMLVideoElement | null>;
  triggers: TriggerEntry[];
  accentColor: string;
  onSeek: (time: number) => void;
}

function SeekBar({ videoRef, triggers, accentColor, onSeek }: SeekBarProps) {
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const rafRef = useRef<number | null>(null);
  const trackRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const vid = videoRef.current;
    if (!vid) return;
    const onMeta = () => setDuration(vid.duration || 0);
    vid.addEventListener('loadedmetadata', onMeta);
    if (vid.readyState >= 1) setDuration(vid.duration || 0);
    return () => vid.removeEventListener('loadedmetadata', onMeta);
  }, [videoRef]);

  useEffect(() => {
    const tick = () => {
      if (videoRef.current && !isDragging) {
        setCurrentTime(videoRef.current.currentTime);
      }
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current !== null) cancelAnimationFrame(rafRef.current); };
  }, [videoRef, isDragging]);

  const getTimeFromEvent = useCallback((e: React.MouseEvent | MouseEvent): number => {
    const track = trackRef.current;
    if (!track || duration <= 0) return 0;
    const rect = track.getBoundingClientRect();
    const x = Math.max(0, Math.min(e.clientX - rect.left, rect.width));
    return (x / rect.width) * duration;
  }, [duration]);

  const handleTrackMouseDown = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
    const t = getTimeFromEvent(e);
    setCurrentTime(t);
    onSeek(t);
  }, [getTimeFromEvent, onSeek]);

  useEffect(() => {
    if (!isDragging) return;
    const onMove = (e: MouseEvent) => {
      const t = getTimeFromEvent(e);
      setCurrentTime(t);
      onSeek(t);
    };
    const onUp = () => setIsDragging(false);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [isDragging, getTimeFromEvent, onSeek]);

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div className="w-full px-1 py-2 select-none">
      {/* Track */}
      <div
        ref={trackRef}
        className="relative w-full rounded-full cursor-pointer"
        style={{ height: 6, background: 'rgba(255,255,255,0.1)' }}
        onMouseDown={handleTrackMouseDown}
      >
        {/* Filled portion */}
        <div
          className="absolute left-0 top-0 h-full rounded-full pointer-events-none"
          style={{ width: `${progress}%`, background: accentColor, transition: isDragging ? 'none' : 'width 0.05s linear' }}
        />
        {/* Trigger markers */}
        {duration > 0 && triggers.map((trigger) => {
          const pct = (trigger.timeSeconds / duration) * 100;
          return (
            <div
              key={trigger.id}
              className="absolute top-1/2 pointer-events-none"
              style={{
                left: `${pct}%`,
                transform: 'translate(-50%, -50%)',
                width: 3,
                height: 12,
                background: '#fff',
                borderRadius: 2,
                boxShadow: `0 0 4px ${accentColor}`,
                opacity: 0.85,
              }}
              title={`Trigger at ${formatTimestamp(trigger.timeSeconds)}`}
            />
          );
        })}
        {/* Scrub handle */}
        <div
          className="absolute top-1/2 pointer-events-none"
          style={{
            left: `${progress}%`,
            transform: 'translate(-50%, -50%)',
            width: isDragging ? 16 : 12,
            height: isDragging ? 16 : 12,
            background: '#fff',
            borderRadius: '50%',
            boxShadow: `0 0 0 2px ${accentColor}, 0 2px 8px rgba(0,0,0,0.5)`,
            transition: isDragging ? 'none' : 'width 0.1s, height 0.1s',
            zIndex: 2,
          }}
        />
      </div>
      {/* Time labels */}
      <div className="flex justify-between mt-1.5">
        <span className="text-xs font-mono" style={{ color: 'rgba(255,255,255,0.35)', fontSize: '10px' }}>
          {formatTimestamp(currentTime)}
        </span>
        <span className="text-xs font-mono" style={{ color: 'rgba(255,255,255,0.25)', fontSize: '10px' }}>
          {duration > 0 ? formatTimestamp(duration) : '--:--.-'}
        </span>
      </div>
    </div>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export default function CustomScenarioPage() {
  // Builder state
  const [step, setStep] = useState<BuilderStep>('load');
  const [videoObjectUrl, setVideoObjectUrl] = useState<string | null>(null);
  const [videoFileName, setVideoFileName] = useState<string>('');
  const [triggers, setTriggers] = useState<TriggerEntry[]>([]);
  const [reactionWindowMs, setReactionWindowMs] = useState<number>(300);
  const [fps, setFps] = useState<number>(30);

  // Scenario name
  const [scenarioName, setScenarioName] = useState<string>('');

  // Saved scenarios
  const [savedScenarios, setSavedScenarios] = useState<SavedScenario[]>([]);
  const [showMyScenariosPanel, setShowMyScenariosPanel] = useState(false);
  const [loadedScenarioId, setLoadedScenarioId] = useState<string | null>(null);
  const [noFilePrompt, setNoFilePrompt] = useState(false);
  const [noFileHint, setNoFileHint] = useState<string>('');

  // Playback state (builder tag step)
  const builderVideoRef = useRef<HTMLVideoElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Play mode state
  const [gameState, setGameState] = useState<'idle' | 'playing' | 'result' | 'summary'>('idle');
  const [resultCard, setResultCard] = useState<ResultCardData | null>(null);
  const [summaryCard, setSummaryCard] = useState<SummaryCardData | null>(null);
  const [floatingFeedbacks, setFloatingFeedbacks] = useState<FloatingFeedback[]>([]);
  const [autoplay, setAutoplay] = useState(false);
  const [currentTriggerIdx, setCurrentTriggerIdx] = useState(0);
  const playVideoRef = useRef<HTMLVideoElement | null>(null);
  const rafRef = useRef<number | null>(null);
  const triggerArmedRef = useRef(false);
  const triggerFiredRef = useRef(false);
  const windowOpenRef = useRef(false);
  const triggerTimestampRef = useRef<number | null>(null);
  const windowCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const currentTriggerIdxRef = useRef<number>(0);
  const gameStateRef = useRef<'idle' | 'playing' | 'result' | 'summary'>('idle');
  const reactionWindowRef = useRef<number>(300);
  const isMultiTriggerRef = useRef<boolean>(false);
  const multiTriggerResultsRef = useRef<TriggerResult[]>([]);
  const feedbackCounterRef = useRef(0);

  // Keep refs in sync
  useEffect(() => { gameStateRef.current = gameState; }, [gameState]);
  useEffect(() => { reactionWindowRef.current = reactionWindowMs; }, [reactionWindowMs]);

  // Load saved scenarios on mount
  useEffect(() => {
    const scenarios = loadScenariosFromStorage();
    setSavedScenarios(scenarios);
  }, []);

  // Cleanup object URL on unmount
  useEffect(() => {
    return () => {
      if (videoObjectUrl) URL.revokeObjectURL(videoObjectUrl);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── File loading ────────────────────────────────────────────────────────────

  const handleFileSelect = useCallback((file: File) => {
    if (videoObjectUrl) URL.revokeObjectURL(videoObjectUrl);
    const url = URL.createObjectURL(file);
    setVideoObjectUrl(url);
    setVideoFileName(file.name);
    setNoFilePrompt(false);
    setNoFileHint('');
    setStep('tag');
  }, [videoObjectUrl]);

  const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFileSelect(file);
  }, [handleFileSelect]);

  // ── Scenario save / load / delete ──────────────────────────────────────────

  const handleSaveScenario = useCallback(() => {
    const name = scenarioName.trim() || `Scenario ${new Date().toLocaleDateString()}`;
    const existing = loadScenariosFromStorage();

    // If we have a loaded scenario ID, update it; otherwise create new
    let updated: SavedScenario[];
    if (loadedScenarioId) {
      updated = existing.map(s =>
        s.id === loadedScenarioId
          ? { ...s, name, triggers, reactionWindowMs, fps, videoFileName, savedAt: Date.now() }
          : s
      );
    } else {
      const newScenario: SavedScenario = {
        id: `scenario-${Date.now()}`,
        name,
        triggers,
        reactionWindowMs,
        fps,
        videoFileName,
        savedAt: Date.now(),
      };
      updated = [newScenario, ...existing];
      setLoadedScenarioId(newScenario.id);
    }
    saveScenariosToStorage(updated);
    setSavedScenarios(updated);
  }, [scenarioName, triggers, reactionWindowMs, fps, videoFileName, loadedScenarioId]);

  const handleLoadScenario = useCallback((scenario: SavedScenario) => {
    setTriggers(scenario.triggers);
    setReactionWindowMs(scenario.reactionWindowMs);
    setFps(scenario.fps || 30);
    setScenarioName(scenario.name);
    setLoadedScenarioId(scenario.id);
    setShowMyScenariosPanel(false);

    // Video file can't be persisted — prompt user to re-select
    if (videoObjectUrl) URL.revokeObjectURL(videoObjectUrl);
    setVideoObjectUrl(null);
    setVideoFileName('');
    setNoFilePrompt(true);
    setNoFileHint(scenario.videoFileName || '');
    setStep('load');
  }, [videoObjectUrl]);

  const handleDeleteScenario = useCallback((id: string) => {
    let updated = loadScenariosFromStorage().filter(s => s.id !== id);
    saveScenariosToStorage(updated);
    setSavedScenarios(updated);
    if (loadedScenarioId === id) setLoadedScenarioId(null);
  }, [loadedScenarioId]);

  // ── Builder controls ────────────────────────────────────────────────────────

  const handleAddTrigger = useCallback(() => {
    const vid = builderVideoRef.current;
    if (!vid) return;
    const t = vid.currentTime;
    const newTrigger: TriggerEntry = { id: `t-${Date.now()}`, timeSeconds: t };
    setTriggers((prev) => [...prev, newTrigger].sort((a, b) => a.timeSeconds - b.timeSeconds));
  }, []);

  const handleDeleteTrigger = useCallback((id: string) => {
    setTriggers((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const handleJumpToTrigger = useCallback((timeSeconds: number) => {
    const vid = builderVideoRef.current;
    if (!vid) return;
    vid.currentTime = timeSeconds;
  }, []);

  const handleFrameStep = useCallback((direction: 1 | -1) => {
    const vid = builderVideoRef.current;
    if (!vid) return;
    vid.pause();
    const frameDuration = 1 / fps;
    vid.currentTime = Math.max(0, vid.currentTime + direction * frameDuration);
  }, [fps]);

  const handleFpsChange = useCallback((val: string) => {
    const n = parseInt(val, 10);
    if (!isNaN(n) && n >= 1 && n <= 120) setFps(n);
  }, []);

  const handleReactionWindowChange = useCallback((val: string) => {
    const n = parseInt(val, 10);
    if (!isNaN(n) && n >= 1 && n <= 600) setReactionWindowMs(n);
  }, []);

  // ── Play mode engine ────────────────────────────────────────────────────────

  const stopRaf = useCallback(() => {
    if (rafRef.current !== null) { cancelAnimationFrame(rafRef.current); rafRef.current = null; }
  }, []);

  const clearWindowTimer = useCallback(() => {
    if (windowCloseTimerRef.current) { clearTimeout(windowCloseTimerRef.current); windowCloseTimerRef.current = null; }
  }, []);

  // ── Multi-trigger resolve (inline, no pause) ──────────────────────────────

  const resolveMultiTrigger = useCallback((outcome: ReactionOutcome, reactionMs: number | null, trigIdx: number, totalTrig: number) => {
    stopRaf();
    clearWindowTimer();
    triggerArmedRef.current = false;
    triggerFiredRef.current = false;
    windowOpenRef.current = false;

    multiTriggerResultsRef.current.push({ triggerIndex: trigIdx, outcome, reactionMs });

    const fbId = ++feedbackCounterRef.current;
    setFloatingFeedbacks(prev => [...prev, { id: fbId, outcome, reactionMs }]);

    const nextTriggerIdx = trigIdx + 1;
    if (nextTriggerIdx < totalTrig) {
      const nextTimeSec = triggers[nextTriggerIdx]?.timeSeconds ?? 0;
      currentTriggerIdxRef.current = nextTriggerIdx;
      setCurrentTriggerIdx(nextTriggerIdx);

      triggerArmedRef.current = true;
      triggerFiredRef.current = false;
      windowOpenRef.current = false;
      triggerTimestampRef.current = null;

      const poll = () => {
        if (!playVideoRef.current || gameStateRef.current !== 'playing') return;
        const ct = playVideoRef.current.currentTime;
        if (!triggerFiredRef.current && ct >= nextTimeSec) {
          triggerFiredRef.current = true;
          triggerTimestampRef.current = performance.now();
          windowOpenRef.current = true;
          windowCloseTimerRef.current = setTimeout(() => {
            if (windowOpenRef.current) {
              windowOpenRef.current = false;
              resolveMultiTrigger('too-late', null, nextTriggerIdx, totalTrig);
            }
          }, reactionWindowRef.current);
        }
        rafRef.current = requestAnimationFrame(poll);
      };
      rafRef.current = requestAnimationFrame(poll);
    } else {
      // All triggers done — stop video immediately and show summary
      if (playVideoRef.current) {
        playVideoRef.current.pause();
      }
      setSummaryCard({ results: [...multiTriggerResultsRef.current] });
      setGameState('summary');
      gameStateRef.current = 'summary';
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stopRaf, clearWindowTimer, triggers]);

  // ── Single-trigger resolve ────────────────────────────────────────────────

  const resolveSingleTrigger = useCallback((outcome: ReactionOutcome, reactionMs: number | null, trigIdx: number, totalTrig: number) => {
    stopRaf();
    clearWindowTimer();
    triggerArmedRef.current = false;
    triggerFiredRef.current = false;
    windowOpenRef.current = false;
    if (outcome !== 'too-late' && playVideoRef.current) {
      playVideoRef.current.pause();
    }
    setResultCard({ outcome, reactionMs, triggerIndex: trigIdx, totalTriggers: totalTrig });
    setGameState('result');
    gameStateRef.current = 'result';
  }, [stopRaf, clearWindowTimer]);

  const startRafLoop = useCallback((triggerTimeSec: number, trigIdx: number, totalTrig: number, isMulti: boolean) => {
    triggerArmedRef.current = true;
    triggerFiredRef.current = false;
    windowOpenRef.current = false;
    triggerTimestampRef.current = null;

    const poll = () => {
      if (!playVideoRef.current || gameStateRef.current !== 'playing') return;
      const ct = playVideoRef.current.currentTime;
      if (!triggerFiredRef.current && ct >= triggerTimeSec) {
        triggerFiredRef.current = true;
        triggerTimestampRef.current = performance.now();
        windowOpenRef.current = true;
        windowCloseTimerRef.current = setTimeout(() => {
          if (windowOpenRef.current) {
            windowOpenRef.current = false;
            if (isMulti) {
              resolveMultiTrigger('too-late', null, trigIdx, totalTrig);
            } else {
              resolveSingleTrigger('too-late', null, trigIdx, totalTrig);
            }
          }
        }, reactionWindowRef.current);
      }
      rafRef.current = requestAnimationFrame(poll);
    };
    rafRef.current = requestAnimationFrame(poll);
  }, [resolveMultiTrigger, resolveSingleTrigger]);

  const startPlayMode = useCallback(() => {
    if (!videoObjectUrl || triggers.length === 0) return;
    stopRaf();
    clearWindowTimer();
    setResultCard(null);
    setSummaryCard(null);
    setFloatingFeedbacks([]);
    multiTriggerResultsRef.current = [];
    setCurrentTriggerIdx(0);
    currentTriggerIdxRef.current = 0;
    const isMulti = triggers.length > 1;
    isMultiTriggerRef.current = isMulti;
    setGameState('playing');
    gameStateRef.current = 'playing';

    const vid = playVideoRef.current;
    if (vid) {
      vid.src = videoObjectUrl;
      vid.currentTime = 0;
      vid.play().catch(() => {});
    }
    startRafLoop(triggers[0].timeSeconds, 0, triggers.length, isMulti);
  }, [videoObjectUrl, triggers, stopRaf, clearWindowTimer, startRafLoop]);

  const handlePlayAreaClick = useCallback(() => {
    if (gameState !== 'playing') return;
    const tIdx = currentTriggerIdxRef.current;
    const total = triggers.length;
    const isMulti = isMultiTriggerRef.current;

    if (!triggerFiredRef.current) {
      if (isMulti) {
        resolveMultiTrigger('too-early', null, tIdx, total);
      } else {
        resolveSingleTrigger('too-early', null, tIdx, total);
      }
      return;
    }
    if (windowOpenRef.current && triggerTimestampRef.current !== null) {
      const reactionMs = Math.round(performance.now() - triggerTimestampRef.current);
      const outcome = classifyReaction(reactionMs);
      if (isMulti) {
        resolveMultiTrigger(outcome, reactionMs, tIdx, total);
      } else {
        resolveSingleTrigger(outcome, reactionMs, tIdx, total);
      }
    }
  }, [gameState, triggers, resolveMultiTrigger, resolveSingleTrigger]);

  const handleNext = useCallback(() => {
    setResultCard(null);
    setSummaryCard(null);
    startPlayMode();
  }, [startPlayMode]);

  const handleRetry = useCallback(() => {
    startPlayMode();
  }, [startPlayMode]);

  const exitPlayMode = useCallback(() => {
    stopRaf();
    clearWindowTimer();
    setGameState('idle');
    gameStateRef.current = 'idle';
    setResultCard(null);
    setSummaryCard(null);
    setFloatingFeedbacks([]);
    if (playVideoRef.current) { playVideoRef.current.pause(); playVideoRef.current.currentTime = 0; }
    setStep('tag');
  }, [stopRaf, clearWindowTimer]);

  const handleClose = useCallback(() => {
    exitPlayMode();
  }, [exitPlayMode]);

  useEffect(() => {
    return () => { stopRaf(); clearWindowTimer(); };
  }, [stopRaf, clearWindowTimer]);

  // ── Render: Play Mode ───────────────────────────────────────────────────────

  if (step === 'play') {
    return (
      <div className="relative w-full h-screen overflow-hidden flex flex-col" style={{ background: '#0a0a0a', fontFamily: 'var(--font-geist), sans-serif' }}>
        {/* Top Stats Bar — visible during play */}
        {gameState === 'playing' && (
          <StatsBarCS
            shots={[]}
            accentColor={ACCENT}
            totalTriggers={triggers.length}
            currentTrigger={currentTriggerIdx}
          />
        )}
        {/* Top bar */}
        <div
          className="absolute z-40 flex items-center justify-between px-3 pointer-events-auto"
          style={{
            top: gameState === 'playing' ? 48 : 0,
            left: 0, right: 0,
            paddingTop: 8, paddingBottom: 8,
            background: gameState === 'playing' ? 'transparent' : 'linear-gradient(to bottom, rgba(10,10,15,0.75) 0%, rgba(10,10,15,0) 100%)',
          }}
          onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center gap-3">
            {/* Logo first */}
            <div className="flex items-center gap-1.5">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#ff4655" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="1" fill="#ff4655" />
                <line x1="12" y1="3" x2="12" y2="6" /><line x1="12" y1="18" x2="12" y2="21" />
                <line x1="3" y1="12" x2="6" y2="12" /><line x1="18" y1="12" x2="21" y2="12" />
              </svg>
              <span className="text-sm font-bold tracking-tight text-white" style={{ letterSpacing: '-0.01em' }}>
                fps<span style={{ color: '#ff4655' }}>.</span>expert
              </span>
            </div>
            {/* Menu button second */}
            <button onClick={exitPlayMode}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-mono font-bold tracking-widest uppercase transition-colors"
              style={{ color: 'rgba(255,255,255,0.6)', background: 'rgba(10,10,15,0.5)', border: '1px solid rgba(255,255,255,0.12)', backdropFilter: 'blur(8px)', cursor: 'pointer' }}
              onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.color = 'rgba(255,255,255,0.9)'; }}
              onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.color = 'rgba(255,255,255,0.6)'; }}>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19 12H5M12 5l-7 7 7 7" />
              </svg>
              Builder
            </button>
          </div>
          <div className="absolute left-1/2 top-1/2 flex items-center gap-2" style={{ transform: 'translate(-50%, -50%)', pointerEvents: 'none' }}>
            <div className="w-1.5 h-1.5 rounded-full" style={{ background: ACCENT, boxShadow: `0 0 6px ${ACCENT}` }} />
            <span className="text-xs font-mono font-bold uppercase tracking-widest" style={{ color: ACCENT, textShadow: `0 0 12px ${ACCENT}88` }}>Custom Scenario</span>
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-mono"
            style={{ background: 'rgba(10,10,15,0.5)', border: '1px solid rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.35)', backdropFilter: 'blur(8px)' }}>
            {triggers.length} trigger{triggers.length !== 1 ? 's' : ''} · {reactionWindowMs}ms window
          </div>
        </div>

        {/* Main play area */}
        <div className="absolute inset-0"
          onClick={gameState !== 'result' && gameState !== 'summary' ? (gameState === 'idle' ? startPlayMode : handlePlayAreaClick) : undefined}
          style={{ cursor: gameState === 'playing' ? 'crosshair' : 'default' }}>

          {/* Idle overlay */}
          {gameState === 'idle' && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-6 z-20" style={{ background: 'rgba(0,0,0,0.72)' }}>
              <div className="flex flex-col items-center gap-4 text-center">
                <div className="flex h-16 w-16 items-center justify-center rounded-2xl"
                  style={{ background: `${ACCENT}15`, border: `1px solid ${ACCENT}30` }}>
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={ACCENT} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="5 3 19 12 5 21 5 3" fill={ACCENT} fillOpacity="0.2" />
                  </svg>
                </div>
                <div>
                  <p className="text-xs font-mono uppercase tracking-widest mb-1" style={{ color: `${ACCENT}99` }}>Custom Scenario</p>
                  <h2 className="text-2xl font-bold text-white mb-2" style={{ letterSpacing: '-0.02em' }}>Ready to train</h2>
                  <p className="text-sm" style={{ color: 'rgba(255,255,255,0.4)' }}>Click anywhere to begin · {triggers.length} trigger{triggers.length !== 1 ? 's' : ''}</p>
                </div>
                <div className="mt-2 px-6 py-2.5 rounded-xl text-sm font-bold uppercase tracking-widest"
                  style={{ background: ACCENT, color: '#fff', letterSpacing: '0.1em', boxShadow: `0 0 20px ${ACCENT}55` }}>
                  Press Start
                </div>
              </div>
            </div>
          )}

          <video ref={playVideoRef} className="absolute inset-0 w-full h-full object-cover" playsInline preload="auto"
            style={{ display: gameState === 'playing' || gameState === 'summary' ? 'block' : 'none' }} />

          {/* No crosshair overlay in video mode — video footage has its own crosshair */}

          {gameState === 'result' && resultCard && (
            <ResultCard
              data={resultCard}
              autoplay={autoplay}
              accentColor={ACCENT}
              onToggleAutoplay={() => setAutoplay((v) => !v)}
              onNext={handleNext}
              onRetry={handleRetry}
              onClose={handleClose}
            />
          )}

          {gameState === 'summary' && summaryCard && (
            <SummaryCard
              data={summaryCard}
              autoplay={autoplay}
              accentColor={ACCENT}
              onToggleAutoplay={() => setAutoplay((v) => !v)}
              onNext={handleNext}
              onRetry={handleRetry}
              onClose={handleClose}
            />
          )}
        </div>

        {/* Bottom bar */}
        <div className="absolute bottom-0 left-0 right-0 z-40 flex items-center justify-center px-4 py-3"
          style={{ background: 'linear-gradient(to top, rgba(10,10,15,0.75) 0%, rgba(10,10,15,0) 100%)' }}
          onClick={(e) => e.stopPropagation()}>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2.5">
              <span className="text-xs font-mono uppercase tracking-widest" style={{ color: 'rgba(255,255,255,0.5)', fontSize: '10px' }}>Autoplay</span>
              <button onClick={() => setAutoplay((v) => !v)}
                className="relative flex items-center rounded-full transition-all duration-200"
                style={{ width: 36, height: 20, background: autoplay ? hexToRgba(ACCENT, 0.3) : 'rgba(255,255,255,0.1)', border: `1px solid ${autoplay ? hexToRgba(ACCENT, 0.5) : 'rgba(255,255,255,0.15)'}`, cursor: 'pointer', padding: 0 }}>
                <span className="absolute rounded-full transition-all duration-200"
                  style={{ width: 14, height: 14, background: autoplay ? ACCENT : 'rgba(255,255,255,0.4)', left: autoplay ? 19 : 2, boxShadow: autoplay ? `0 0 6px ${hexToRgba(ACCENT, 0.6)}` : 'none' }} />
              </button>
              <span className="text-xs font-mono" style={{ color: autoplay ? ACCENT : 'rgba(255,255,255,0.25)', fontSize: '10px' }}>{autoplay ? 'ON' : 'OFF'}</span>
            </div>
            <div style={{ width: 1, height: 20, background: 'rgba(255,255,255,0.15)' }} />
            <button onClick={() => { if (gameState === 'idle') startPlayMode(); else exitPlayMode(); }}
              className="flex items-center gap-2 px-5 py-2 rounded-xl text-xs font-bold uppercase tracking-widest transition-all"
              style={{ background: gameState !== 'idle' ? 'rgba(255,70,85,0.2)' : hexToRgba(ACCENT, 0.22), border: `1px solid ${gameState !== 'idle' ? 'rgba(255,70,85,0.5)' : hexToRgba(ACCENT, 0.5)}`, color: gameState !== 'idle' ? '#ff4655' : ACCENT, cursor: 'pointer', letterSpacing: '0.1em', backdropFilter: 'blur(8px)' }}>
              {gameState !== 'idle' ? (
                <><svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><rect x="6" y="4" width="4" height="16" /><rect x="14" y="4" width="4" height="16" /></svg>Stop</>
              ) : (
                <><svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3" /></svg>Play</>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Render: Builder (load / tag steps) ─────────────────────────────────────

  return (
    <div className="min-h-screen" style={{ background: '#0a0a0a', fontFamily: 'var(--font-geist), sans-serif' }}>
      {/* Noise texture */}
      <div className="pointer-events-none fixed inset-0 z-0 opacity-[0.025]"
        style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`, backgroundSize: '128px 128px' }} />

      {/* Header — logo FIRST, then Menu button */}
      <header className="relative z-10 flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
        <div className="flex items-center gap-3">
          {/* Logo first */}
          <div className="flex items-center gap-1.5">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#ff4655" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="1" fill="#ff4655" />
              <line x1="12" y1="3" x2="12" y2="6" /><line x1="12" y1="18" x2="12" y2="21" />
              <line x1="3" y1="12" x2="6" y2="12" /><line x1="18" y1="12" x2="21" y2="12" />
            </svg>
            <span className="text-sm font-bold tracking-tight text-white" style={{ letterSpacing: '-0.01em' }}>
              fps<span style={{ color: '#ff4655' }}>.</span>expert
            </span>
          </div>
          {/* Menu button second */}
          <Link href="/" className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-mono font-bold tracking-widest uppercase transition-colors"
            style={{ color: 'rgba(255,255,255,0.5)', background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)', textDecoration: 'none' }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = 'rgba(255,255,255,0.85)'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = 'rgba(255,255,255,0.5)'; }}>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5M12 5l-7 7 7 7" />
            </svg>
            Menu
          </Link>
        </div>
        <div className="flex items-center gap-3">
          {/* My Scenarios button */}
          <button
            onClick={() => setShowMyScenariosPanel(v => !v)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-mono font-bold tracking-widest uppercase transition-colors"
            style={{
              color: showMyScenariosPanel ? ACCENT : 'rgba(255,255,255,0.5)',
              background: showMyScenariosPanel ? hexToRgba(ACCENT, 0.1) : 'rgba(255,255,255,0.04)',
              border: `1px solid ${showMyScenariosPanel ? hexToRgba(ACCENT, 0.35) : 'rgba(255,255,255,0.08)'}`,
              cursor: 'pointer',
            }}>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M3 6h18M3 12h18M3 18h18" />
            </svg>
            My Scenarios {savedScenarios.length > 0 && `(${savedScenarios.length})`}
          </button>
          <div className="flex items-center gap-2">
            <div className="w-1.5 h-1.5 rounded-full" style={{ background: ACCENT, boxShadow: `0 0 6px ${ACCENT}` }} />
            <span className="text-xs font-mono font-bold uppercase tracking-widest" style={{ color: ACCENT }}>Custom Scenario Builder</span>
          </div>
        </div>
      </header>

      {/* My Scenarios Panel */}
      {showMyScenariosPanel && (
        <div className="relative z-10 border-b mx-6 my-0" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
          <div className="py-4">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-bold text-white">My Scenarios</p>
              <p className="text-xs font-mono" style={{ color: 'rgba(255,255,255,0.3)' }}>Saved locally in your browser</p>
            </div>
            {savedScenarios.length === 0 ? (
              <div className="flex items-center gap-2.5 px-4 py-3 rounded-xl" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)' }}>
                <p className="text-xs" style={{ color: 'rgba(255,255,255,0.35)' }}>No saved scenarios yet. Build one and click &quot;Save Scenario&quot; to keep it.</p>
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                {savedScenarios.map(scenario => (
                  <div key={scenario.id} className="flex items-center gap-3 px-4 py-3 rounded-xl"
                    style={{
                      background: loadedScenarioId === scenario.id ? hexToRgba(ACCENT, 0.07) : 'rgba(255,255,255,0.03)',
                      border: `1px solid ${loadedScenarioId === scenario.id ? hexToRgba(ACCENT, 0.25) : 'rgba(255,255,255,0.06)'}`,
                    }}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <p className="text-sm font-semibold text-white truncate">{scenario.name}</p>
                        {loadedScenarioId === scenario.id && (
                          <span className="text-xs px-1.5 py-0.5 rounded-full flex-shrink-0" style={{ background: hexToRgba(ACCENT, 0.15), color: ACCENT, border: `1px solid ${hexToRgba(ACCENT, 0.3)}` }}>Active</span>
                        )}
                      </div>
                      <p className="text-xs font-mono" style={{ color: 'rgba(255,255,255,0.35)' }}>
                        {scenario.triggers.length} trigger{scenario.triggers.length !== 1 ? 's' : ''} · {scenario.reactionWindowMs}ms window
                        {scenario.videoFileName && <span style={{ color: 'rgba(255,255,255,0.25)' }}> · {scenario.videoFileName}</span>}
                      </p>
                    </div>
                    <button
                      onClick={() => handleLoadScenario(scenario)}
                      className="flex-shrink-0 px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-widest transition-all"
                      style={{ background: hexToRgba(ACCENT, 0.12), border: `1px solid ${hexToRgba(ACCENT, 0.3)}`, color: ACCENT, cursor: 'pointer' }}
                      onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = hexToRgba(ACCENT, 0.22); }}
                      onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = hexToRgba(ACCENT, 0.12); }}>
                      Load
                    </button>
                    <button
                      onClick={() => handleDeleteScenario(scenario.id)}
                      className="flex-shrink-0 px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-widest transition-all"
                      style={{ background: 'rgba(255,70,85,0.08)', border: '1px solid rgba(255,70,85,0.2)', color: '#ff4655', cursor: 'pointer' }}
                      onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,70,85,0.18)'; }}
                      onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,70,85,0.08)'; }}>
                      Delete
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <main className="relative z-10 max-w-3xl mx-auto px-4 py-8">
        {/* Privacy notice */}
        <div className="mb-6 flex items-center gap-2.5 px-4 py-3 rounded-xl"
          style={{ background: 'rgba(255,140,66,0.07)', border: '1px solid rgba(255,140,66,0.2)' }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={ACCENT} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
          </svg>
          <p className="text-xs" style={{ color: 'rgba(255,255,255,0.55)' }}>
            Your video stays on your device and is never uploaded. All processing is local-only.
          </p>
        </div>

        {/* Re-select video prompt (after loading a saved scenario) */}
        {noFilePrompt && !videoObjectUrl && (
          <div className="mb-6 flex items-start gap-3 px-4 py-4 rounded-xl"
            style={{ background: 'rgba(0,229,255,0.06)', border: '1px solid rgba(0,229,255,0.2)' }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#00e5ff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0 mt-0.5">
              <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
            </svg>
            <div>
              <p className="text-sm font-semibold text-white mb-0.5">Scenario config loaded</p>
              <p className="text-xs" style={{ color: 'rgba(255,255,255,0.5)' }}>
                {noFileHint
                  ? <>Re-select <span className="font-mono text-white">&apos;{noFileHint}&apos;</span> to continue — your triggers and settings are intact.</>
                  : <>Re-select your video file to continue — your config is preserved.</>
                }
              </p>
            </div>
          </div>
        )}

        {/* Scenario Name + Save */}
        <section className="mb-6">
          <div className="rounded-2xl p-4" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
            <div className="flex items-center gap-3">
              <div className="flex-1 min-w-0">
                <label className="block text-xs font-mono uppercase tracking-widest mb-1.5" style={{ color: 'rgba(255,255,255,0.4)' }}>Scenario Name</label>
                <input
                  type="text"
                  value={scenarioName}
                  onChange={(e) => setScenarioName(e.target.value)}
                  placeholder="e.g. Ascent A-site peek"
                  className="w-full rounded-lg px-3 py-2 text-sm"
                  style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.85)', outline: 'none', fontFamily: 'inherit' }}
                />
              </div>
              <button
                onClick={handleSaveScenario}
                disabled={triggers.length === 0}
                className="flex-shrink-0 flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-widest transition-all mt-5"
                style={{
                  background: triggers.length > 0 ? hexToRgba(ACCENT, 0.15) : 'rgba(255,255,255,0.04)',
                  border: `1px solid ${triggers.length > 0 ? hexToRgba(ACCENT, 0.4) : 'rgba(255,255,255,0.08)'}`,
                  color: triggers.length > 0 ? ACCENT : 'rgba(255,255,255,0.25)',
                  cursor: triggers.length > 0 ? 'pointer' : 'not-allowed',
                }}
                onMouseEnter={(e) => { if (triggers.length > 0) (e.currentTarget as HTMLButtonElement).style.background = hexToRgba(ACCENT, 0.25); }}
                onMouseLeave={(e) => { if (triggers.length > 0) (e.currentTarget as HTMLButtonElement).style.background = hexToRgba(ACCENT, 0.15); }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z" /><polyline points="17 21 17 13 7 13 7 21" /><polyline points="7 3 7 8 15 8" />
                </svg>
                {loadedScenarioId ? 'Update' : 'Save'}
              </button>
            </div>
          </div>
        </section>

        {/* Step: Load */}
        <section className="mb-8">
          <div className="flex items-center gap-3 mb-4">
            <div className="flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold"
              style={{ background: step === 'load' ? ACCENT : 'rgba(255,255,255,0.08)', color: step === 'load' ? '#fff' : 'rgba(255,255,255,0.4)' }}>1</div>
            <h2 className="text-base font-bold text-white">Load Video</h2>
            {videoObjectUrl && (
              <span className="text-xs font-mono px-2 py-0.5 rounded-full" style={{ background: 'rgba(0,229,255,0.1)', color: '#00e5ff', border: '1px solid rgba(0,229,255,0.25)' }}>
                ✓ Loaded
              </span>
            )}
          </div>

          <div className="rounded-2xl p-5" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
            {videoObjectUrl ? (
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl" style={{ background: `${ACCENT}14`, color: ACCENT }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M15 10l4.553-2.069A1 1 0 0121 8.87v6.26a1 1 0 01-1.447.899L15 14" /><rect x="3" y="6" width="12" height="12" rx="2" />
                    </svg>
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-white truncate">{videoFileName}</p>
                    <p className="text-xs" style={{ color: 'rgba(255,255,255,0.35)' }}>Local file · never uploaded</p>
                  </div>
                </div>
                <button onClick={() => fileInputRef.current?.click()}
                  className="flex-shrink-0 px-3 py-1.5 rounded-lg text-xs font-semibold uppercase tracking-widest transition-all"
                  style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.55)', cursor: 'pointer' }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.color = 'rgba(255,255,255,0.85)'; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.color = 'rgba(255,255,255,0.55)'; }}>
                  Change
                </button>
              </div>
            ) : (
              <button onClick={() => fileInputRef.current?.click()}
                className="w-full flex flex-col items-center gap-3 py-8 rounded-xl transition-all"
                style={{ background: 'transparent', border: `2px dashed rgba(255,140,66,0.25)`, cursor: 'pointer' }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(255,140,66,0.5)'; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.borderColor = 'rgba(255,140,66,0.25)'; }}>
                <div className="flex h-12 w-12 items-center justify-center rounded-xl" style={{ background: `${ACCENT}14`, color: ACCENT }}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" /><polyline points="17 8 12 3 7 8" /><line x1="12" y1="3" x2="12" y2="15" />
                  </svg>
                </div>
                <div className="text-center">
                  <p className="text-sm font-semibold text-white mb-0.5">Select a video file</p>
                  <p className="text-xs" style={{ color: 'rgba(255,255,255,0.35)' }}>MP4, WebM, MOV — stays on your device</p>
                </div>
              </button>
            )}
            <input ref={fileInputRef} type="file" accept="video/*" className="hidden" onChange={handleFileInputChange} />
          </div>
        </section>

        {/* Step: Tag */}
        {videoObjectUrl && (
          <section className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold"
                style={{ background: ACCENT, color: '#fff' }}>2</div>
              <h2 className="text-base font-bold text-white">Tag Reaction Moments</h2>
            </div>

            <div className="rounded-2xl overflow-hidden" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
              <div className="relative bg-black" style={{ aspectRatio: '16/9' }}>
                <video ref={builderVideoRef} src={videoObjectUrl} className="w-full h-full object-contain" playsInline preload="auto" />
              </div>
              {/* Seek bar with trigger markers */}
              <div className="px-4 pt-3 pb-0">
                <SeekBar
                  videoRef={builderVideoRef}
                  triggers={triggers}
                  accentColor={ACCENT}
                  onSeek={(t) => {
                    if (builderVideoRef.current) {
                      builderVideoRef.current.currentTime = t;
                    }
                  }}
                />
              </div>
              <div className="p-4 space-y-4">
                <div className="flex items-center gap-2 flex-wrap">
                  <button onClick={() => { const v = builderVideoRef.current; if (v) { if (v.paused) v.play().catch(() => {}); else v.pause(); } }}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-bold uppercase tracking-widest transition-all"
                    style={{ background: `${ACCENT}18`, border: `1px solid ${ACCENT}40`, color: ACCENT, cursor: 'pointer' }}
                    onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = `${ACCENT}30`; }}
                    onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = `${ACCENT}18`; }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><polygon points="5 3 19 12 5 21 5 3" /></svg>
                    Play / Pause
                  </button>
                  <button onClick={() => handleFrameStep(-1)}
                    className="flex items-center gap-1 px-3 py-2 rounded-lg text-xs font-bold transition-all"
                    style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.7)', cursor: 'pointer' }}
                    onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.1)'; }}
                    onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.05)'; }}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 5l-7 7 7 7" /></svg>
                    ‹ Frame
                  </button>
                  <button onClick={() => handleFrameStep(1)}
                    className="flex items-center gap-1 px-3 py-2 rounded-lg text-xs font-bold transition-all"
                    style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.7)', cursor: 'pointer' }}
                    onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.1)'; }}
                    onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,255,255,0.05)'; }}>
                    Frame ›
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12h14M12 5l7 7-7 7" /></svg>
                  </button>
                  <div className="flex items-center gap-1.5 ml-auto">
                    <span className="text-xs font-mono uppercase tracking-widest" style={{ color: 'rgba(255,255,255,0.4)' }}>FPS</span>
                    <input type="number" min={1} max={120} value={fps}
                      onChange={(e) => handleFpsChange(e.target.value)}
                      className="w-14 rounded-lg px-2 py-1.5 text-xs font-mono text-center"
                      style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.8)', outline: 'none' }} />
                  </div>
                </div>
                <TimestampReadout videoRef={builderVideoRef} />
                <button onClick={handleAddTrigger}
                  className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-bold uppercase tracking-widest transition-all"
                  style={{ background: `${ACCENT}18`, border: `1.5px solid ${ACCENT}50`, color: ACCENT, cursor: 'pointer', letterSpacing: '0.08em' }}
                  onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = `${ACCENT}30`; }}
                  onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = `${ACCENT}18`; }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
                  </svg>
                  Add Trigger at Current Time
                </button>
              </div>
            </div>

            {triggers.length > 0 && (
              <div className="mt-4 rounded-2xl overflow-hidden" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)' }}>
                <div className="px-4 py-3 border-b flex items-center justify-between" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
                  <p className="text-xs font-mono uppercase tracking-widest" style={{ color: 'rgba(255,255,255,0.4)' }}>
                    Triggers ({triggers.length})
                  </p>
                  <p className="text-xs" style={{ color: 'rgba(255,255,255,0.25)' }}>Auto-sorted by time</p>
                </div>
                <div className="divide-y" style={{ borderColor: 'rgba(255,255,255,0.04)' }}>
                  {triggers.map((trigger, idx) => (
                    <div key={trigger.id} className="flex items-center gap-3 px-4 py-3">
                      <span className="text-xs font-mono w-5 text-center flex-shrink-0" style={{ color: 'rgba(255,255,255,0.3)' }}>{idx + 1}</span>
                      <span className="flex-1 text-sm font-mono font-semibold text-white">{formatTimestamp(trigger.timeSeconds)}</span>
                      <span className="text-xs font-mono" style={{ color: 'rgba(255,255,255,0.3)' }}>{trigger.timeSeconds.toFixed(3)}s</span>
                      <button onClick={() => handleJumpToTrigger(trigger.timeSeconds)}
                        className="px-2.5 py-1 rounded-lg text-xs font-semibold transition-all"
                        style={{ background: 'rgba(0,229,255,0.08)', border: '1px solid rgba(0,229,255,0.2)', color: '#00e5ff', cursor: 'pointer' }}
                        onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(0,229,255,0.18)'; }}
                        onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(0,229,255,0.08)'; }}>
                        Jump
                      </button>
                      <button onClick={() => handleDeleteTrigger(trigger.id)}
                        className="px-2.5 py-1 rounded-lg text-xs font-semibold transition-all"
                        style={{ background: 'rgba(255,70,85,0.08)', border: '1px solid rgba(255,70,85,0.2)', color: '#ff4655', cursor: 'pointer' }}
                        onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,70,85,0.18)'; }}
                        onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'rgba(255,70,85,0.08)'; }}>
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </section>
        )}

        {/* Step: Settings + Play */}
        {videoObjectUrl && (
          <section className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold"
                style={{ background: ACCENT, color: '#fff' }}>3</div>
              <h2 className="text-base font-bold text-white">Reaction Window &amp; Play</h2>
            </div>

            <div className="rounded-2xl p-5 space-y-5" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)' }}>
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-semibold text-white">Reaction Window</label>
                  <span className="text-sm font-mono font-bold" style={{ color: ACCENT }}>{reactionWindowMs} ms</span>
                </div>
                <input type="range" min={1} max={600} value={reactionWindowMs}
                  onChange={(e) => setReactionWindowMs(parseInt(e.target.value, 10))}
                  className="w-full h-1.5 rounded-full appearance-none cursor-pointer"
                  style={{ accentColor: ACCENT }} />
                <div className="flex justify-between mt-1">
                  <span className="text-xs font-mono" style={{ color: 'rgba(255,255,255,0.25)' }}>1ms</span>
                  <span className="text-xs font-mono" style={{ color: 'rgba(255,255,255,0.25)' }}>600ms</span>
                </div>
                <div className="mt-2 flex items-center gap-2">
                  <input type="number" min={1} max={600} value={reactionWindowMs}
                    onChange={(e) => handleReactionWindowChange(e.target.value)}
                    className="w-20 rounded-lg px-2 py-1.5 text-xs font-mono text-center"
                    style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.8)', outline: 'none' }} />
                  <span className="text-xs" style={{ color: 'rgba(255,255,255,0.35)' }}>ms (same range as built-in modes)</span>
                </div>
              </div>

              <button
                onClick={() => { if (triggers.length === 0) return; setStep('play'); setTimeout(() => startPlayMode(), 50); }}
                disabled={triggers.length === 0}
                className="w-full flex items-center justify-center gap-2.5 py-4 rounded-xl text-sm font-bold uppercase tracking-widest transition-all"
                style={{
                  background: triggers.length > 0 ? ACCENT : 'rgba(255,255,255,0.05)',
                  border: `1.5px solid ${triggers.length > 0 ? ACCENT : 'rgba(255,255,255,0.08)'}`,
                  color: triggers.length > 0 ? '#fff' : 'rgba(255,255,255,0.25)',
                  cursor: triggers.length > 0 ? 'pointer' : 'not-allowed',
                  letterSpacing: '0.1em',
                  boxShadow: triggers.length > 0 ? `0 0 24px ${ACCENT}55` : 'none',
                  opacity: triggers.length > 0 ? 1 : 0.5,
                }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <polygon points="5 3 19 12 5 21 5 3" />
                </svg>
                {triggers.length === 0 ? 'Add triggers to play' : `Play Scenario · ${triggers.length} trigger${triggers.length !== 1 ? 's' : ''}`}
              </button>
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
